import React, { useState, useMemo } from 'react';
import { formatNumber } from '../shared/UI';
import { MASTER_DAPIL_DATA } from '../../lib/dapilMasterData';
import {
  Calculator, Trophy, Sparkles, TrendingUp, AlertCircle, CheckCircle2,
  Sliders, RotateCcw, Award, Users, ChevronRight, HelpCircle
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../ui/table';

// Alokasi Kursi resmi DPRD Kabupaten Sukabumi per Dapil (Total 50 Kursi)
export const DAPIL_SEATS = {
  'Dapil 1': 7,
  'Dapil 2': 8,
  'Dapil 3': 9,
  'Dapil 4': 10,
  'Dapil 5': 7,
  'Dapil 6': 9,
};

// Estimasi Perolehan Suara Partai Pileg 2024 DPRD Kab. Sukabumi per Dapil
// Berdasarkan hasil resmi KPU & baseline kursi 2024
export const BASELINE_PARTAI_2024 = {
  'Dapil 1': [
    { partai: 'Golkar', suara: 38200, warna: '#EAB308' },
    { partai: 'Gerindra', suara: 35100, warna: '#EF4444' },
    { partai: 'PKB', suara: 27800, warna: '#10B981' },
    { partai: 'PDIP', suara: 24500, warna: '#DC2626' },
    { partai: 'Demokrat', suara: 21200, warna: '#2563EB' },
    { partai: 'PKS', suara: 15719, warna: '#EA580C', isPks: true },
    { partai: 'PPP', suara: 15400, warna: '#059669' },
    { partai: 'PAN', suara: 12100, warna: '#0284C7' },
  ],
  'Dapil 2': [
    { partai: 'Golkar', suara: 42300, warna: '#EAB308' },
    { partai: 'Gerindra', suara: 39500, warna: '#EF4444' },
    { partai: 'PKB', suara: 31200, warna: '#10B981' },
    { partai: 'PKS', suara: 23392, warna: '#EA580C', isPks: true },
    { partai: 'PDIP', suara: 22100, warna: '#DC2626' },
    { partai: 'Demokrat', suara: 20400, warna: '#2563EB' },
    { partai: 'PPP', suara: 18900, warna: '#059669' },
    { partai: 'PAN', suara: 14500, warna: '#0284C7' },
  ],
  'Dapil 3': [
    { partai: 'Golkar', suara: 46100, warna: '#EAB308' },
    { partai: 'Gerindra', suara: 41200, warna: '#EF4444' },
    { partai: 'PKS', suara: 29914, warna: '#EA580C', isPks: true },
    { partai: 'PKB', suara: 28500, warna: '#10B981' },
    { partai: 'PDIP', suara: 25400, warna: '#DC2626' },
    { partai: 'Demokrat', suara: 22800, warna: '#2563EB' },
    { partai: 'PPP', suara: 21300, warna: '#059669' },
    { partai: 'PAN', suara: 16900, warna: '#0284C7' },
  ],
  'Dapil 4': [
    { partai: 'PKS', suara: 52788, warna: '#EA580C', isPks: true }, // Juara di Dapil 4 pada 2024 (2 Kursi)
    { partai: 'Golkar', suara: 44200, warna: '#EAB308' },
    { partai: 'Gerindra', suara: 39800, warna: '#EF4444' },
    { partai: 'PKB', suara: 32400, warna: '#10B981' },
    { partai: 'PDIP', suara: 26100, warna: '#DC2626' },
    { partai: 'Demokrat', suara: 25700, warna: '#2563EB' },
    { partai: 'PAN', suara: 21800, warna: '#0284C7' },
    { partai: 'PPP', suara: 19500, warna: '#059669' },
  ],
  'Dapil 5': [
    { partai: 'Golkar', suara: 36700, warna: '#EAB308' },
    { partai: 'Gerindra', suara: 32400, warna: '#EF4444' },
    { partai: 'PKB', suara: 25900, warna: '#10B981' },
    { partai: 'PKS', suara: 23377, warna: '#EA580C', isPks: true },
    { partai: 'PDIP', suara: 21800, warna: '#DC2626' },
    { partai: 'PPP', suara: 17200, warna: '#059669' },
    { partai: 'Demokrat', suara: 16500, warna: '#2563EB' },
    { partai: 'PAN', suara: 11200, warna: '#0284C7' },
  ],
  'Dapil 6': [
    { partai: 'Golkar', suara: 41900, warna: '#EAB308' },
    { partai: 'Gerindra', suara: 37500, warna: '#EF4444' },
    { partai: 'PKB', suara: 30100, warna: '#10B981' },
    { partai: 'PDIP', suara: 25800, warna: '#DC2626' },
    { partai: 'PPP', suara: 23400, warna: '#059669' },
    { partai: 'Demokrat', suara: 19800, warna: '#2563EB' },
    { partai: 'PKS', suara: 16114, warna: '#EA580C', isPks: true },
    { partai: 'PAN', suara: 14700, warna: '#0284C7' },
  ],
};

// Hitung Alokasi Sainte-Laguë Murni
export function runSainteLague(parties, totalSeats) {
  const quotients = [];
  // Pembagi ganjil: 1, 3, 5, 7, 9, 11...
  parties.forEach((p) => {
    for (let s = 1; s <= totalSeats; s++) {
      const divisor = 2 * s - 1;
      quotients.push({
        partai: p.partai,
        suaraAsli: p.suara,
        divisor,
        quotient: Math.round(p.suara / divisor),
        isPks: !!p.isPks,
        warna: p.warna,
        kursiKe: s,
      });
    }
  });

  // Urutkan nilai bagi dari terbesar ke terkecil
  quotients.sort((a, b) => b.quotient - a.quotient);

  // Ambil N kursi pemenang
  const winningQuotients = quotients.slice(0, totalSeats);

  // Rekapitulasi per partai
  const seatsByParty = {};
  parties.forEach((p) => {
    seatsByParty[p.partai] = 0;
  });

  winningQuotients.forEach((w, index) => {
    w.urutanKursi = index + 1;
    seatsByParty[w.partai] = (seatsByParty[w.partai] || 0) + 1;
  });

  const lastWinningQuotient = winningQuotients[totalSeats - 1] || null;
  const runnerUpQuotient = quotients[totalSeats] || null;

  return {
    winningQuotients,
    seatsByParty,
    lastWinningQuotient,
    runnerUpQuotient,
    allQuotients: quotients,
  };
}

const SainteLagueSimulator = ({ initialDapil = 'Dapil 3' }) => {
  const [selectedDapil, setSelectedDapil] = useState(initialDapil);
  const totalSeats = DAPIL_SEATS[selectedDapil] || 9;

  const targetPlan = useMemo(() => {
    return MASTER_DAPIL_DATA.find((d) => d.dapil === selectedDapil) || MASTER_DAPIL_DATA[2];
  }, [selectedDapil]);

  // State suara simulasi per partai
  const [partyVotes, setPartyVotes] = useState(() => {
    const initial = {};
    Object.keys(BASELINE_PARTAI_2024).forEach((dap) => {
      initial[dap] = BASELINE_PARTAI_2024[dap].map((p) => ({ ...p }));
    });
    return initial;
  });

  const currentParties = useMemo(() => {
    return partyVotes[selectedDapil] || [];
  }, [partyVotes, selectedDapil]);

  // Update suara partai tertentu
  const updateVote = (partaiName, newVotes) => {
    const val = Math.max(0, parseInt(newVotes, 10) || 0);
    setPartyVotes((prev) => ({
      ...prev,
      [selectedDapil]: prev[selectedDapil].map((p) =>
        p.partai === partaiName ? { ...p, suara: val } : p
      ),
    }));
  };

  // Reset ke Baseline 2024
  const resetToBaseline = () => {
    setPartyVotes((prev) => ({
      ...prev,
      [selectedDapil]: BASELINE_PARTAI_2024[selectedDapil].map((p) => ({ ...p })),
    }));
  };

  // Terapkan Target Resmi BP3 2029
  const applyBp3Target = () => {
    const targetSuara = targetPlan.target_suara_2029;
    setPartyVotes((prev) => ({
      ...prev,
      [selectedDapil]: prev[selectedDapil].map((p) =>
        p.isPks ? { ...p, suara: targetSuara } : p
      ),
    }));
  };

  // Skenario Ambang Batas Tambahan
  const applyThresholdScenario = () => {
    const ambang = targetPlan.ambang_tambahan || (targetPlan.baseline_2024 * 1.5);
    setPartyVotes((prev) => ({
      ...prev,
      [selectedDapil]: prev[selectedDapil].map((p) =>
        p.isPks ? { ...p, suara: ambang + 500 } : p
      ),
    }));
  };

  // Jalankan kalkulasi Sainte-Laguë
  const simResult = useMemo(() => {
    return runSainteLague(currentParties, totalSeats);
  }, [currentParties, totalSeats]);

  const pksSeats = simResult.seatsByParty['PKS'] || 0;
  const pksParty = currentParties.find((p) => p.isPks) || { suara: 0 };
  const targetSeats = targetPlan.target_kursi_2029;

  // Analisis Gap & Ambang Batas
  const statusAnalysis = useMemo(() => {
    const isTargetAchieved = pksSeats >= targetSeats;
    const isSpecialFocus = selectedDapil === 'Dapil 3' || selectedDapil === 'Dapil 4';

    // Cari quotient kursi PKS yang lolos
    const pksWonRounds = simResult.winningQuotients.filter((w) => w.isPks);

    // Hitung margin keamanan ke kursi berikutnya
    const lastWon = pksWonRounds[pksWonRounds.length - 1];
    const lastSeatOverall = simResult.lastWinningQuotient;

    let marginText = '';
    if (isTargetAchieved) {
      if (lastWon && simResult.runnerUpQuotient) {
        const diff = lastWon.quotient - simResult.runnerUpQuotient.quotient;
        marginText = `Aman dengan keunggulan ${formatNumber(diff)} nilai bagi di atas kursi runner-up.`;
      }
    } else {
      // Butuh berapa lagi agar tembus kursi target?
      const targetDivisor = 2 * targetSeats - 1;
      const minQuotientNeeded = (lastSeatOverall?.quotient || 0) + 1;
      const minVotesNeeded = minQuotientNeeded * targetDivisor;
      const shortage = Math.max(0, minVotesNeeded - pksParty.suara);
      marginText = `Kurang sekitar ${formatNumber(shortage)} suara lagi untuk merebut kursi ke-${targetSeats}.`;
    }

    return {
      isTargetAchieved,
      isSpecialFocus,
      marginText,
      pksWonRounds,
    };
  }, [pksSeats, targetSeats, selectedDapil, simResult, pksParty.suara]);

  return (
    <div className="space-y-6">
      {/* HEADER SIMULATOR */}
      <div className="bg-gradient-to-r from-gray-900 via-slate-800 to-orange-950 p-6 rounded-2xl text-white shadow-md relative overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2.5 h-2.5 rounded-full bg-orange-500 animate-pulse"></span>
              <span className="text-xs font-black tracking-widest text-orange-400 uppercase">
                SIMULATOR SAINTE-LAGUË 2029
              </span>
              <Badge className="bg-white/10 text-white font-bold text-[10px] border-white/20">
                What-If Analysis
              </Badge>
            </div>
            <h2 className="text-xl md:text-2xl font-black tracking-tight">
              Kalkulator Perolehan Kursi & Ambang Batas {selectedDapil}
            </h2>
            <p className="text-xs text-gray-300 font-medium mt-1 max-w-2xl">
              Uji perolehan suara PKS secara dinamis terhadap pembagi Sainte-Laguë (1, 3, 5, 7, ...). Pantau apakah target 2 kursi (Dapil 3) atau 3 kursi (Dapil 4) berhasil terkunci.
            </p>
          </div>

          {/* Quick Result Badge */}
          <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md p-3.5 rounded-2xl border border-white/15">
            <div className="text-right">
              <p className="text-[10px] font-bold text-gray-300 uppercase tracking-wider">Perolehan Kursi PKS</p>
              <div className="flex items-baseline justify-end gap-1.5 mt-0.5">
                <span className="text-3xl font-black text-orange-400">{pksSeats}</span>
                <span className="text-xs text-gray-300 font-bold">/ Target {targetSeats} Kursi</span>
              </div>
            </div>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg ${
              statusAnalysis.isTargetAchieved
                ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-400/50'
                : 'bg-amber-500/30 text-amber-300 border border-amber-400/50'
            }`}>
              {statusAnalysis.isTargetAchieved ? '✓' : '!'}
            </div>
          </div>
        </div>

        {/* DAPIL TABS SELECTOR */}
        <div className="flex items-center gap-1.5 overflow-x-auto mt-6 pt-4 border-t border-white/10 scrollbar-none">
          {Object.keys(DAPIL_SEATS).map((dap) => {
            const isSelected = selectedDapil === dap;
            const seats = DAPIL_SEATS[dap];
            const isPriority = dap === 'Dapil 3' || dap === 'Dapil 4';

            return (
              <button
                key={dap}
                onClick={() => setSelectedDapil(dap)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex-shrink-0 flex items-center gap-1.5 ${
                  isSelected
                    ? 'bg-orange-500 text-white shadow-md shadow-orange-500/30 scale-105'
                    : 'bg-white/10 text-gray-200 hover:bg-white/20'
                }`}
              >
                <span>{dap} ({seats} Kursi)</span>
                {isPriority && (
                  <span className="text-[10px] px-1 py-0.2 bg-amber-400 text-gray-950 rounded font-black">
                    {dap === 'Dapil 3' ? '+1' : '+1'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* DUA KOLOM: CONTROLLER SUARA (KIRI) & HASIL ALOKASI KURSI (KANAN) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* KOLOM KIRI: SLIDER & INPUT SIMULASI SUARA (5 COLS) */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-white rounded-2xl p-5 card-shadow border border-gray-100">
            <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2">
                <Sliders className="w-4 h-4 text-orange-600" />
                <h3 className="font-black text-sm text-gray-900">Kontrol Suara {selectedDapil}</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetToBaseline}
                className="h-7 text-xs font-semibold text-gray-500 hover:text-orange-600 gap-1"
              >
                <RotateCcw className="w-3 h-3" />
                Reset
              </Button>
            </div>

            {/* PRESET BUTTONS KHUSUS PKS */}
            <div className="mb-4 p-3 bg-orange-50/70 rounded-xl border border-orange-200 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold text-orange-950 flex items-center gap-1.5">
                  <Award className="w-4 h-4 text-orange-600" />
                  Skenario Suara PKS
                </span>
                <span className="text-xs font-black text-orange-600">
                  {formatNumber(pksParty.suara)} Suara
                </span>
              </div>

              {/* Slider Suara PKS */}
              <input
                type="range"
                min={Math.round(targetPlan.baseline_2024 * 0.7)}
                max={Math.round(targetPlan.target_suara_2029 * 1.4)}
                step={250}
                value={pksParty.suara}
                onChange={(e) => updateVote('PKS', e.target.value)}
                className="w-full accent-orange-600 cursor-pointer"
              />

              <div className="flex flex-wrap gap-1.5 pt-1">
                <button
                  type="button"
                  onClick={resetToBaseline}
                  className="px-2 py-1 text-[10px] font-bold rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"
                >
                  Baseline 2024 ({formatNumber(targetPlan.baseline_2024)})
                </button>
                <button
                  type="button"
                  onClick={applyBp3Target}
                  className="px-2 py-1 text-[10px] font-bold rounded-lg bg-orange-600 text-white hover:bg-orange-700 shadow-xs"
                >
                  Target BP3 2029 ({formatNumber(targetPlan.target_suara_2029)})
                </button>
                {targetPlan.ambang_tambahan && (
                  <button
                    type="button"
                    onClick={applyThresholdScenario}
                    className="px-2 py-1 text-[10px] font-bold rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Ambang Kursi Tambahan (&gt; {formatNumber(targetPlan.ambang_tambahan)})
                  </button>
                )}
              </div>
            </div>

            {/* TABEL INPUT SUARA SELURUH PARTAI */}
            <p className="text-[11px] font-bold text-gray-500 mb-2 uppercase tracking-wider">
              Estimasi Suara Seluruh Partai (Dapat Diubah)
            </p>
            <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
              {currentParties.map((p) => {
                const won = simResult.seatsByParty[p.partai] || 0;
                return (
                  <div
                    key={p.partai}
                    className={`flex items-center justify-between p-2 rounded-xl border text-xs ${
                      p.isPks
                        ? 'bg-orange-50/50 border-orange-300 font-bold'
                        : 'bg-white border-gray-100 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p.warna }} />
                      <span className={`truncate ${p.isPks ? 'text-orange-950 font-black' : 'text-gray-800 font-bold'}`}>
                        {p.partai}
                      </span>
                      {won > 0 && (
                        <Badge className={`text-[10px] py-0 px-1.5 h-4 font-black ${
                          p.isPks ? 'bg-orange-600 text-white' : 'bg-gray-800 text-white'
                        }`}>
                          {won} Kursi
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Input
                        type="number"
                        min="0"
                        step="500"
                        value={p.suara}
                        onChange={(e) => updateVote(p.partai, e.target.value)}
                        className={`h-7 w-28 text-right text-xs font-bold rounded-lg ${
                          p.isPks ? 'border-orange-400 font-black text-orange-700' : 'border-gray-200'
                        }`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* KOLOM KANAN: HASIL SAINTE-LAGUE & ALOKASI KURSI (7 COLS) */}
        <div className="lg:col-span-7 space-y-4">
          {/* KARTU STATUS & KESIMPULAN AMBANG */}
          <div className={`p-5 rounded-2xl border ${
            statusAnalysis.isTargetAchieved
              ? 'bg-emerald-50/70 border-emerald-300'
              : 'bg-amber-50/80 border-amber-300'
          }`}>
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                statusAnalysis.isTargetAchieved ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'
              }`}>
                {statusAnalysis.isTargetAchieved ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <h4 className={`font-black text-sm ${
                    statusAnalysis.isTargetAchieved ? 'text-emerald-950' : 'text-amber-950'
                  }`}>
                    {statusAnalysis.isTargetAchieved
                      ? `Target ${targetSeats} Kursi di ${selectedDapil} BERHASIL TERCAPAI!`
                      : `Target ${targetSeats} Kursi di ${selectedDapil} BELUM TERCAPAI (Saat Ini: ${pksSeats} Kursi)`}
                  </h4>
                  <Badge className={statusAnalysis.isTargetAchieved ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'}>
                    {pksSeats} dari {targetSeats} Kursi
                  </Badge>
                </div>
                <p className="text-xs text-gray-700 font-medium mt-1">
                  {statusAnalysis.marginText}
                </p>

                {/* Info Ambang Batas Resmi dari Dokumen PPT */}
                {targetPlan.ambang_tambahan && (
                  <div className="mt-2.5 pt-2.5 border-t border-black/10 flex items-center justify-between text-xs">
                    <span className="font-bold text-gray-700">
                      Ambang Batas Target Kursi Tambahan (PPT):
                    </span>
                    <span className="font-black text-blue-800">
                      &gt; {formatNumber(targetPlan.ambang_tambahan)} Suara
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* TABEL URUTAN KURSI TERBAGI (1 s/d N Kursi) */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-black text-sm text-gray-900 flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-500" />
                  Urutan Alokasi {totalSeats} Kursi Sainte-Laguë {selectedDapil}
                </h3>
                <p className="text-xs text-gray-500 font-medium">
                  Setiap kursi diperoleh oleh nilai hasil bagi tertinggi secara berturut-turut.
                </p>
              </div>
              <Badge variant="outline" className="text-xs font-bold text-gray-700">
                Total {totalSeats} Kursi
              </Badge>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead className="font-extrabold text-xs py-2 px-3 text-center w-12">Kursi</TableHead>
                    <TableHead className="font-extrabold text-xs py-2 px-3">Partai Pemenang</TableHead>
                    <TableHead className="font-extrabold text-xs py-2 px-3 text-center">Kursi Ke (Partai)</TableHead>
                    <TableHead className="font-extrabold text-xs py-2 px-3 text-center">Pembagi</TableHead>
                    <TableHead className="font-extrabold text-xs py-2 px-3 text-right">Nilai Hasil Bagi</TableHead>
                    <TableHead className="font-extrabold text-xs py-2 px-3 text-right">Suara Asli</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="divide-y divide-gray-100">
                  {simResult.winningQuotients.map((w) => {
                    const isPks = w.isPks;
                    return (
                      <TableRow
                        key={`kursi-${w.urutanKursi}`}
                        className={`text-xs ${
                          isPks
                            ? 'bg-orange-50/80 font-bold hover:bg-orange-100/70'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <TableCell className="py-2.5 px-3 text-center font-black">
                          <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs ${
                            isPks ? 'bg-orange-600 text-white font-black' : 'bg-gray-200 text-gray-800'
                          }`}>
                            {w.urutanKursi}
                          </span>
                        </TableCell>
                        <TableCell className="py-2.5 px-3">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: w.warna }} />
                            <span className={isPks ? 'font-black text-orange-950 text-xs' : 'font-bold text-gray-900'}>
                              {w.partai}
                            </span>
                            {isPks && (
                              <Badge className="bg-orange-600 text-white font-bold text-[9px] py-0 px-1">
                                PKS
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-2.5 px-3 text-center font-bold">
                          Kursi ke-{w.kursiKe}
                        </TableCell>
                        <TableCell className="py-2.5 px-3 text-center font-mono font-bold text-gray-500">
                          :{w.divisor}
                        </TableCell>
                        <TableCell className={`py-2.5 px-3 text-right font-mono font-black ${
                          isPks ? 'text-orange-700' : 'text-gray-900'
                        }`}>
                          {formatNumber(w.quotient)}
                        </TableCell>
                        <TableCell className="py-2.5 px-3 text-right text-gray-500 font-medium">
                          {formatNumber(w.suaraAsli)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Kursi Terakhir & Runner-Up Info */}
            {simResult.runnerUpQuotient && (
              <div className="p-3 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between text-xs text-gray-600 font-medium">
                <span className="flex items-center gap-1.5">
                  <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
                  <span>Ambang batas kursi terakhir (Kursi ke-{totalSeats}):</span>
                  <b>{formatNumber(simResult.lastWinningQuotient?.quotient || 0)}</b>
                </span>
                <span>
                  Gagal lolos (Runner-Up): <b className="text-rose-600">{simResult.runnerUpQuotient.partai}</b> ({formatNumber(simResult.runnerUpQuotient.quotient)})
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SainteLagueSimulator;
